import React from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Label, HelperMessage } from '@atlaskit/form';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { Skeleton } from '@atlassian/jira-common-components-skeleton';
import { gridSize } from '@atlassian/jira-common-styles/src';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { FieldContainer } from '@atlassian/jira-issue-create-commons';
import type { StatusTransition } from '@atlassian/jira-issue-field-status';
import { StatusView } from '@atlassian/jira-issue-field-status/src/ui/status-view';
import { InfoIcon } from './changeboarding';
import messages from './messages';

export type Props = {
	isLoading: boolean;
	error?: Error;
	transitions: StatusTransition[];
	value: StatusTransition | null;
	onChange?: (
		transition: StatusTransition,
		analyticsEvent: UIAnalyticsEvent,
	) => void | Promise<void>;
	isMini: boolean;
};

const statusFieldMargin = gridSize * 2.25;

const StatusField = (props: Props) => {
	const { formatMessage } = useIntl();

	const defaultProps = {
		isLoading: false,
		footer: undefined,
		fieldId: 'gic-status-field',
	};

	const getEnabledTransitions = (transitions: StatusTransition[]) =>
		transitions.filter((transition) => transition.isDisabled === false);

	const getFieldContent = () => {
		if (props.isLoading) return <FieldSkeleton isMini={props.isMini} aria-busy />;

		if (!props.value?.to) {
			// error state
			return <FieldErrorSkeleton isMini={props.isMini} />;
		}

		if (props.error) return <FieldErrorSkeleton isMini={props.isMini} />;

		return (
			<StatusView
				{...defaultProps}
				appearance="button"
				value={props.value?.to}
				selectedTransition={props.value}
				transitions={getEnabledTransitions(props.transitions)}
				forceNoLabels
				isDefaultSelect={props.isMini}
				onChange={props.onChange}
				customNoOptionsMessage={formatMessage(messages.noEnabledTransitionsMessage)}
				shouldFitContainer={!ff('create-issue-status-button-bug-fix-jiv-17893_hg8fs')}
			/>
		);
	};

	const getHelpOrWarningText = () => {
		if (props.error) {
			return (
				<HelperMessage>
					<WarningIcon
						size="small"
						primaryColor={token('color.icon.warning', colors.Y500)}
						label={formatMessage(messages.warningIconLabel)}
					/>
					{formatMessage(messages.warningMessage)}
				</HelperMessage>
			);
		}
		if (props.isMini) {
			return null;
		}
		return <HelperText>{formatMessage(messages.helperText)}</HelperText>;
	};

	return (
		<FieldContainer marginTop={statusFieldMargin}>
			<Label htmlFor={defaultProps.fieldId} id="gic-status-field-label">
				<LabelFlex>
					{formatMessage(messages.statusFieldLabel)}
					{!props.isMini && <InfoIcon />}
				</LabelFlex>
			</Label>
			{getFieldContent()}
			{getHelpOrWarningText()}
		</FieldContainer>
	);
};

export default StatusField;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HelperText = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N200),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	fontSize: `${gridSize * 1.25}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	lineHeight: `${gridSize * 2}px`,
	marginTop: token('space.075', '6px'),
	marginBottom: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldSkeleton = styled(Skeleton)<{ isMini?: boolean }>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isMini }) =>
		isMini
			? {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					height: `${gridSize * 5}px`,
					width: '100%',
				}
			: {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					height: `${gridSize * 4}px`,
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					width: `${gridSize * 15}px`,
				},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	{ marginBottom: `${gridSize * 0.75}px` },
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldErrorSkeleton = styled.div<{ isMini?: boolean }>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isMini }) =>
		isMini
			? {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					height: `${gridSize * 5}px`,
					width: '100%',
				}
			: {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					height: `${gridSize * 4}px`,
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					width: `${gridSize * 15}px`,
				},
	{
		marginBottom: token('space.075', '6px'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('color.skeleton', colors.N30),
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LabelFlex = styled.div({
	display: 'flex',
	alignItems: 'flex-start',
});
